/**
 * Shared pieces for the home-practice screens (tranche 4B). Teal only: practice is an effort
 * signal and never shares a colour with a mastery band (spec §5.1, `test/palette.test.ts`).
 */

import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getToken, api } from '../../lib/api';
import { S } from '../../design/kit';

export const TEAL = 'var(--c-practice)';
export const TEAL_2 = 'var(--c-practice-2)';
export const TEAL_3 = 'var(--c-practice-3)';
export const TEAL_WASH = 'var(--c-practice-wash)';

/** The teal strip every practice screen opens with: what this is, and what it is not. */
export function PracticeBanner({ title, body, tag = 'Effort signal' }: { title: string; body: ReactNode; tag?: string }) {
  return (
    <div role="note" style={{ background: TEAL_WASH, border: `1px solid ${TEAL_3}`, borderRadius: '12px', padding: '14px 18px', display: 'flex', gap: '14px', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap' }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ ...S.strong, color: TEAL }}>{title}</div>
        <div style={{ ...S.small, color: 'var(--c-ink-2)', marginTop: '3px' }}>{body}</div>
      </div>
      <TealChip>{tag}</TealChip>
    </div>
  );
}

export function TealChip({ children }: { children: ReactNode }) {
  return <span style={{ fontSize: '0.6875rem', fontWeight: 600, color: TEAL, background: TEAL_WASH, border: `1px solid ${TEAL_3}`, padding: '4px 8px', borderRadius: '5px', whiteSpace: 'nowrap', flex: 'none' }}>{children}</span>;
}

/** C.2: the home's practice strip. Effort only: streak, sessions, badges, never a mark or a band. */
export function PracticeStrip({ streak, sessions, badges }: { streak: number; sessions: number; badges: number }) {
  const stat = (value: number, label: string) => (
    <div>
      <div style={{ fontSize: '1.375rem', fontWeight: 650, color: TEAL, lineHeight: 1.1 }}>{value}</div>
      <div style={S.mutedXs}>{label}</div>
    </div>
  );
  return (
    <section aria-label="Practice activity" style={{ background: TEAL_WASH, border: `1px solid ${TEAL_3}`, borderRadius: '12px', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '22px', flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: '220px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
          <span style={{ ...S.strong, color: 'var(--c-ink)' }}>Practice activity</span>
          <TealChip>Effort, not marks</TealChip>
        </div>
        <div style={{ ...S.small, color: 'var(--c-ink-2)', marginTop: '4px' }}>Home practice never changes your mastery band. It shows how consistently you are working.</div>
      </div>
      {stat(streak, 'day streak')}
      {stat(sessions, 'sessions')}
      {stat(badges, 'badges')}
      <Link to="/student/practice" className="kit-btn" style={{ background: TEAL, color: '#fff', fontSize: '0.8125rem', fontWeight: 600, padding: '0 16px', minHeight: '38px', borderRadius: '9px', display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}>
        Practice now
      </Link>
    </section>
  );
}

/** Criterion averages out of 5, as horizontal teal bars with the figure beside each. */
export function TealBars({ rows, max = 5 }: { rows: { label: string; value: number }[]; max?: number }) {
  return (
    <div style={{ display: 'grid', gap: '9px' }}>
      {rows.map((r) => (
        <div key={r.label} style={{ display: 'grid', gridTemplateColumns: 'minmax(110px, 170px) 1fr 34px', gap: '10px', alignItems: 'center' }}>
          <span style={{ fontSize: '0.7812rem' }}>{r.label}</span>
          <div role="img" aria-label={`${r.label}: ${r.value} of ${max}`} style={{ height: '9px', borderRadius: '5px', background: 'var(--c-track)' }}>
            <div style={{ width: `${Math.min(100, (r.value / max) * 100)}%`, height: '100%', borderRadius: '5px', background: TEAL }} />
          </div>
          <span style={{ fontSize: '0.75rem', fontWeight: 600, textAlign: 'right' }}>{r.value}</span>
        </div>
      ))}
    </div>
  );
}

/** C.6 / C.11 / C.10 sit under one nav item; these are its tabs. */
export function PracticeTabs() {
  const { pathname } = useLocation();
  const tabs = [
    { to: '/student/practice', label: 'Choose a topic' },
    { to: '/student/practice/standings', label: 'Standings' },
    { to: '/student/practice/bank', label: 'Question bank' },
  ];
  return (
    <nav aria-label="Practice" style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
      {tabs.map((t) => {
        const on = pathname === t.to;
        return (
          <Link key={t.to} to={t.to} aria-current={on ? 'page' : undefined} style={{ fontSize: '0.7812rem', fontWeight: 600, padding: '8px 13px', borderRadius: '8px', textDecoration: 'none', color: on ? '#fff' : TEAL, background: on ? TEAL : TEAL_WASH }}>
            {t.label}
          </Link>
        );
      })}
    </nav>
  );
}

/**
 * D-5 upload: ask the API for a target, PUT the bytes (a signed bucket URL in production, the
 * API itself locally), then have the API read them back for size and sha256.
 */
export async function uploadMedia(kind: 'sketch' | 'audio' | 'document' | 'photo', blob: Blob, opts: { durationSec?: number; questionId?: string } = {}): Promise<string> {
  if (blob.size > 50 * 1024 * 1024) throw new Error('Files must be 50 MB or smaller');
  const mime = blob.type || (kind === 'audio' ? 'audio/webm' : 'image/png');
  const made = await api.post<{ id: string; upload: { url: string; method: 'PUT'; headers: Record<string, string>; direct: boolean } }>('/media', {
    kind, mime, bytes: blob.size, ...(opts.durationSec ? { durationSec: opts.durationSec } : {}), ...(opts.questionId ? { questionId: opts.questionId } : {}),
  });
  const token = getToken();
  const res = await fetch(made.upload.url, {
    method: 'PUT',
    headers: { ...made.upload.headers, ...(!made.upload.direct && token ? { authorization: `Bearer ${token}` } : {}) },
    body: blob,
  });
  if (!res.ok) throw new Error('The upload did not go through. Try again.');
  if (made.upload.direct) await api.post(`/media/${made.id}/complete`);
  return made.id;
}

export function clock(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
